import type { GetState, SetState } from 'zustand';
import type { ProductPageProps } from '@product/ProductPage/ProductPage';
import type { ProductPageStore } from './useProductPageStore';
import type { ProductDataSliceData } from '@hooks/states/productDataSlice';
import { GraphQlPriceType } from '@models/graphql';
import type { PriceFragmentEnhanced, ProductFragmentEnhanced } from '@models/enhanced-fragments';
import { getProductPriceType } from '@libs/product.helper';
import { getProductLengthPrice, getProductBasePrice } from '@libs/product-price-utils';

export type PricesSliceData = {
  priceData: PriceData;
};

export type PriceData = {
  priceType: GraphQlPriceType;
  basePricePerUnit: number; // in cent
  additionalLengthPrice: number | null;
  currencyCode: string; // should be the official currency code
  discountQuantity: Array<PriceFragmentEnhanced> | null;
  fetchingDiscountPricesStatus: 'idle' | 'loading' | 'success' | 'error';
};

export interface PricesSlice extends PricesSliceData {
  setPriceData: (data: PriceData) => void;
  setPriceType: (data: GraphQlPriceType) => void;
}

export const initializePriceData = (currentProduct: ProductFragmentEnhanced): PriceData => {
  if ([GraphQlPriceType.LeadScrewLength].includes(getProductPriceType(currentProduct))) {
    return {
      additionalLengthPrice: null,
      basePricePerUnit: 0,
      currencyCode: currentProduct.prices?.[0]?.currency_code,
      discountQuantity: null,
      fetchingDiscountPricesStatus: 'loading',
      priceType: getProductPriceType(currentProduct),
    };
  }
  return {
    additionalLengthPrice: getProductLengthPrice(currentProduct, getProductPriceType(currentProduct)) || null,
    basePricePerUnit: getProductBasePrice(currentProduct),
    currencyCode: currentProduct.prices?.[0]?.currency_code,
    discountQuantity: currentProduct.prices || null,
    fetchingDiscountPricesStatus: 'idle',
    priceType: getProductPriceType(currentProduct),
  };
};

export const createPriceDataSlice = (
  set: SetState<ProductPageStore>,
  get: GetState<ProductPageStore>,
  initialData: ProductPageProps & Partial<ProductDataSliceData>
) =>
  ({
    priceType: getProductPriceType(initialData.currentProduct),
    priceData: initializePriceData(initialData.currentProduct),
    setPriceData: data => {
      set(state => ({
        ...state,
        priceData: data,
      }));
    },
    setPriceType: data => {
      set(state => ({ ...state, priceData: { ...state.priceData, priceType: data } }));
    },
  } as PricesSlice);
